<template>
	<div class="cvu">
		<div class="field-eyebrow">Tipos de documento habilitados</div>
		<p class="cvu-hint">
			Estas cuentas las alimenta el archivo de Ventas/Costos Devengados, así
			que los documentos de otro origen clasificados en ellas
			<strong>no suman</strong> en el reporte. Marca los tipos de documento
			que sí deben sumar.
		</p>

		<ul class="cvu-accounts">
			<li v-for="account in accounts" :key="account.id" class="cvu-account">
				{{ account.level_3 }}
			</li>
		</ul>

		<ul class="cvu-types">
			<li v-for="type in types" :key="type.id" class="cvu-type">
				<label class="cvu-check">
					<input
						type="checkbox"
						:value="type.id"
						:checked="isSelected(type.id)"
						:disabled="saving"
						@change="toggle(type.id)"
					/>
					<span class="cvu-name">{{ type.label || type.name }}</span>
					<span v-if="type.from_sii" class="cvu-tag">SII</span>
					<span class="cvu-count">{{ documentsLabel(type.documents) }}</span>
				</label>
			</li>
		</ul>

		<p v-if="warnsAboutDoubleCount" class="cvu-warning">
			Marcaste tipos que llegan desde el SII. Si esas ventas también vienen
			en el archivo, van a quedar contadas dos veces. Revisa que el archivo
			no las incluya antes de guardar.
		</p>

		<div class="cvu-actions">
			<button
				v-if="dirty"
				type="button"
				class="theme_btn_white"
				:disabled="saving"
				@click="reset"
			>
				Descartar cambios
			</button>
			<button
				type="button"
				class="theme_btn_dark"
				:disabled="!dirty || saving"
				@click="save"
			>
				{{ saving ? "Guardando…" : "Guardar" }}
			</button>
		</div>
	</div>
</template>

<script>
// Configuración de la válvula del bloqueo de Ventas/Costos Devengados
// (docs/DECISIONES.md §15 D6): el archivo es dueño exclusivo de sus cuentas y
// acá el analista habilita, por tipo de documento, qué más puede sumar en
// ellas. Presentacional como el resto de companyInfo: recibe la configuración
// por props y emite `save` con la lista completa — la vista habla con la API.
export default {
	name: "CvUnblockSection",
	props: {
		// [{ id, level_3 }] — las cuentas que hoy alimenta el archivo.
		accounts: { type: Array, default: () => [] },
		// [{ id, name, label, sii_code, from_sii, documents, unblocked }]
		// `label` es el nombre visible: los tipos internos viajan con su valor
		// de matching (`remuneracion-clf`) y esa jerga no va a pantalla.
		types: { type: Array, default: () => [] },
		unblockedIds: { type: Array, default: () => [] },
		saving: { type: Boolean, default: false },
	},
	data() {
		return {
			// Copia local editable; `unblockedIds` sigue siendo la verdad.
			selected: [...this.unblockedIds],
		};
	},
	computed: {
		dirty() {
			return this.sorted(this.selected).join(",") !== this.sorted(this.unblockedIds).join(",");
		},
		warnsAboutDoubleCount() {
			return this.types.some((type) => type.from_sii && this.isSelected(type.id));
		},
	},
	watch: {
		// La vista repone la lista tras guardar (o al recargar la sección).
		unblockedIds(ids) {
			this.selected = [...ids];
		},
	},
	methods: {
		isSelected(id) {
			return this.selected.indexOf(id) !== -1;
		},
		toggle(id) {
			this.selected = this.isSelected(id)
				? this.selected.filter((selectedId) => selectedId !== id)
				: this.selected.concat(id);
		},
		reset() {
			this.selected = [...this.unblockedIds];
		},
		save() {
			this.$emit("save", this.sorted(this.selected));
		},
		sorted(ids) {
			return [...ids].sort((a, b) => a - b);
		},
		documentsLabel(documents) {
			const total = documents || 0;
			return `${total.toLocaleString("es-CL")} ${total === 1 ? "documento" : "documentos"}`;
		},
	},
};
</script>

<style lang="scss" scoped>
@use "./account_plan_buttons" as *;

.cvu-hint {
	margin: 0 0 10px;
	font-size: 12.5px;
	line-height: 18px;
	color: var(--ap-slate);
}

// Las cuentas afectadas: contexto, no acción — pills tenues.
.cvu-accounts {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin: 0 0 12px;
	padding: 0;
	list-style: none;
}

.cvu-account {
	padding: 2px 10px;
	font-size: 12px;
	font-weight: 600;
	color: var(--ap-teal-deep);
	background-color: var(--ap-teal-wash);
	border-radius: var(--ap-radius-pill);
}

.cvu-types {
	margin: 0;
	padding: 0;
	list-style: none;
}

.cvu-type {
	border-top: 1px solid var(--ap-line);
}

.cvu-type:last-child {
	border-bottom: 1px solid var(--ap-line);
}

.cvu-check {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 7px 0;
	cursor: pointer;
}

.cvu-name {
	flex: 1;
	min-width: 0;
	font-size: 13px;
	color: var(--ap-ink-2);
	overflow-wrap: anywhere;
}

.cvu-tag {
	flex: none;
	padding: 1px 7px;
	font-size: 10.5px;
	font-weight: 600;
	letter-spacing: 0.04em;
	color: var(--ap-warn-ink);
	background-color: var(--ap-warn-wash);
	border-radius: var(--ap-radius-pill);
}

.cvu-count {
	flex: none;
	font-size: 12px;
	font-variant-numeric: tabular-nums;
	color: var(--ap-muted);
}

.cvu-warning {
	margin: 10px 0 0;
	padding: 8px 10px;
	font-size: 12.5px;
	line-height: 18px;
	color: var(--ap-warn-ink);
	background-color: var(--ap-warn-wash);
	border-radius: var(--ap-radius-sm);
}

.cvu-actions {
	display: flex;
	justify-content: flex-end;
	gap: 8px;
	margin-top: 12px;
}
</style>
